import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { botApi } from '../../services/botApi';

const {
  FiX, FiTrendingUp, FiTrendingDown, FiShield, FiTarget,
  FiActivity, FiArrowDownRight, FiClock, FiAward, FiSettings, FiSave, FiRotateCcw,
  FiZap, FiAlertTriangle
} = FiIcons;

export default function PositionDetailsDrawer({ position, isOpen, onClose, onSell, settings, onSaveStrategy, notify }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [realSellLoading, setRealSellLoading] = useState(false);
  const [forceCloseConfirm, setForceCloseConfirm] = useState(false);
  const [forceCloseLoading, setForceCloseLoading] = useState(false);

  // Local states for Visual Strategy editor
  const [localHardSL, setLocalHardSL] = useState(25);
  const [localTrailingSL, setLocalTrailingSL] = useState(30);
  const [localTrailingAct, setLocalTrailingAct] = useState(2.0);

  // Sync settings when drawer opens or position changes
  useEffect(() => {
    if (position) {
      setLocalHardSL(position.customHardSL ?? settings.hardStopLoss);
      setLocalTrailingSL(position.customTrailingSL ?? settings.trailingStopLoss);
      setLocalTrailingAct(position.customTrailingAct ?? settings.trailingActivationMultiplier);
      setActiveTab('overview');
    }
  }, [position, settings, isOpen]);

  if (!position) return null;

  const valueMultiplier = position.currentPrice / position.buyPrice;
  const pnlPercentage = (valueMultiplier - 1) * 100;
  const pnlSol = (position.amountSol * valueMultiplier) - position.amountSol;
  const isProfit = pnlPercentage >= 0;
  const currentValueSol = position.amountSol * valueMultiplier;

  // Effective Settings for Display
  const effHardSL = position.customHardSL ?? settings.hardStopLoss;
  const effTrailingSL = position.customTrailingSL ?? settings.trailingStopLoss;
  const effTrailingAct = position.customTrailingAct ?? settings.trailingActivationMultiplier;

  // Stop Loss Calculations
  const hardStopPrice = position.buyPrice * (1 - effHardSL / 100);
  // Peak-based, matching the engine: trailing stays active after the peak hits
  // the activation multiplier, even if price later pulls back below it.
  const isTrailingActive = position.buyPrice > 0 && (position.peakPrice / position.buyPrice) >= effTrailingAct;
  const trailingStopPrice = isTrailingActive ? position.peakPrice * (1 - effTrailingSL / 100) : null;
  const activationPrice = position.buyPrice * effTrailingAct;

  const hardSlProgress = Math.max(0, Math.min(100, ((position.currentPrice - hardStopPrice) / (position.buyPrice - hardStopPrice)) * 100));
  let trailingProgress = 0;
  if (isTrailingActive) {
    trailingProgress = Math.max(0, Math.min(100, ((position.currentPrice - trailingStopPrice) / (position.peakPrice - trailingStopPrice)) * 100));
  } else {
    trailingProgress = Math.max(0, Math.min(100, ((position.currentPrice - position.buyPrice) / (activationPrice - position.buyPrice)) * 100));
  }

  // Profit Milestones Calculations
  const sortedAlerts = [...(settings.profitAlerts || [50, 100, 200, 500])].sort((a, b) => a - b);
  const nextMilestone = sortedAlerts.find(a => a > pnlPercentage);
  const prevMilestone = [...sortedAlerts].reverse().find(a => a <= pnlPercentage) || 0;
  
  let milestoneProgress = 100;
  let nextTargetPrice = null;
  if (nextMilestone) {
    milestoneProgress = Math.max(0, Math.min(100, ((pnlPercentage - prevMilestone) / (nextMilestone - prevMilestone)) * 100));
    nextTargetPrice = position.buyPrice * (1 + nextMilestone / 100);
  }

  const formatPrice = (price) => parseFloat(price).toLocaleString('en-US', { maximumSignificantDigits: 6 });

  const handleSellClick = async (percent) => {
    if (!position.isReal) { onSell(percent); return; }
    // Live position: the backend bot executes the on-chain sell immediately (no confirm).
    if (realSellLoading) return;
    setRealSellLoading(true);
    try {
      const { txid } = await botApi.sell({ positionId: position.id, percentage: percent });
      if (notify) notify(`Live Sold ${percent}% of $${position.symbol} — ${txid.slice(0, 8)}...`, 'success');
      onClose();
    } catch (err) {
      if (notify) notify(`Live sell failed: ${err.message || 'error'}`, 'error');
    } finally {
      setRealSellLoading(false);
    }
  };

  const executeForceClose = async () => {
    setForceCloseLoading(true);
    try {
      const r = await botApi.close({ positionId: position.id });
      if (notify) {
        notify(
          r?.closedWithoutSwap
            ? `Force-closed $${position.symbol} (no on-chain balance to sell)`
            : `Force-closed $${position.symbol}${r?.txid ? ` — ${r.txid.slice(0, 8)}...` : ''}`,
          'success'
        );
      }
      setForceCloseConfirm(false);
      onClose();
    } catch (err) {
      if (notify) notify(`Force close failed: ${err.message || 'error'}`, 'error');
    } finally {
      setForceCloseLoading(false);
    }
  };

  const handleSaveStrategy = () => {
    onSaveStrategy({
      customHardSL: localHardSL,
      customTrailingSL: localTrailingSL,
      customTrailingAct: localTrailingAct
    });
    setActiveTab('overview');
  };

  const handleResetStrategy = () => {
    onSaveStrategy({ customHardSL: null, customTrailingSL: null, customTrailingAct: null });
    setLocalHardSL(settings.hardStopLoss);
    setLocalTrailingSL(settings.trailingStopLoss);
    setLocalTrailingAct(settings.trailingActivationMultiplier);
    setActiveTab('overview');
  };

  const hasCustomSettings = position.customHardSL !== null || position.customTrailingSL !== null || position.customTrailingAct !== null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
        <React.Fragment key="drawer">
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
          />
          <motion.div
            key="panel"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-[#1c1c1d] rounded-t-3xl z-50 border-t border-[#2c2c2e] pb-safe flex flex-col max-h-[90vh]"
          >
            {/* Drawer Header */}
            <div className="flex justify-between items-center p-5 border-b border-[#2c2c2e] shrink-0">
              <div>
                <h3 className="text-white font-bold text-xl flex items-center gap-2">
                  <SafeIcon icon={FiActivity} className="text-blue-400" /> ${position.symbol} Analysis
                </h3>
                <p className="text-gray-500 text-xs font-mono mt-1 text-ellipsis overflow-hidden max-w-[200px]">
                  {position.address}
                </p>
              </div>
              <button 
                onClick={onClose}
                className="w-8 h-8 bg-[#2c2c2e] rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex px-5 pt-3 gap-2 shrink-0">
              <button 
                onClick={() => setActiveTab('overview')}
                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors border ${activeTab === 'overview' ? 'bg-[#2c2c2e] text-white border-[#3c3c3e]' : 'bg-transparent text-gray-500 border-transparent hover:text-gray-300'}`}
              >
                Overview
              </button>
              <button 
                onClick={() => setActiveTab('strategy')}
                className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors border flex items-center justify-center gap-1.5 ${activeTab === 'strategy' ? 'bg-[#2c2c2e] text-white border-[#3c3c3e]' : 'bg-transparent text-gray-500 border-transparent hover:text-gray-300'}`}
              >
                <SafeIcon icon={FiSettings} /> Visual SL
                {hasCustomSettings && <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>}
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-5 overflow-y-auto scrollbar-hide flex-1 space-y-5">
              
              {activeTab === 'overview' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                  {/* Live PnL Hero */}
                  <div className="bg-[#0a0a0b] border border-[#2c2c2e] rounded-2xl p-5 relative overflow-hidden">
                    <div className={`absolute top-0 left-0 w-1 h-full ${isProfit ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <div className="flex justify-between items-end">
                      <div>
                        <h4 className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">Live P&L</h4>
                        <div className={`text-3xl font-black font-mono flex items-center gap-2 ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                          {isProfit ? '+' : ''}{pnlSol.toFixed(4)} <span className="text-lg">SOL</span>
                        </div>
                        <div className={`text-sm font-bold mt-1 ${isProfit ? 'text-green-500/80' : 'text-red-500/80'} flex items-center gap-1`}>
                          <SafeIcon icon={isProfit ? FiTrendingUp : FiTrendingDown} />
                          {isProfit ? '+' : ''}{pnlPercentage.toFixed(2)}%
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-500 text-[10px] uppercase font-semibold mb-1">Current Value</div>
                        <div className="text-white font-mono font-bold">{currentValueSol.toFixed(3)} SOL</div>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#2c2c2e]/30 rounded-xl p-3 border border-[#3c3c3e]/50">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Entry Price</div>
                      <div className="text-white font-mono text-sm">
                        {position.buyPriceUsd ? `$${formatPrice(position.buyPriceUsd)}` : formatPrice(position.buyPrice)}
                      </div>
                    </div>
                    <div className="bg-[#2c2c2e]/30 rounded-xl p-3 border border-[#3c3c3e]/50">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 flex justify-between">
                        Current Price <span className="text-blue-400 animate-pulse">Live</span>
                      </div>
                      <div className={`font-mono text-sm ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
                        {position.currentPriceUsd ? `$${formatPrice(position.currentPriceUsd)}` : formatPrice(position.currentPrice)}
                      </div>
                    </div>
                    <div className="bg-[#2c2c2e]/30 rounded-xl p-3 border border-[#3c3c3e]/50">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Peak Price</div>
                      <div className="text-white font-mono text-sm">
                        {position.peakPriceUsd ? `$${formatPrice(position.peakPriceUsd)}` : formatPrice(position.peakPrice)}
                      </div>
                    </div>
                    <div className="bg-[#2c2c2e]/30 rounded-xl p-3 border border-[#3c3c3e]/50">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Drop from Peak</div>
                      <div className="text-orange-400 font-mono text-sm flex items-center gap-1">
                        <SafeIcon icon={FiArrowDownRight} />
                        {(((position.peakPrice - position.currentPrice) / position.peakPrice) * 100).toFixed(2)}%
                      </div>
                    </div>
                  </div>

                  {/* Auto Take-Profit Tiers Fired */}
                  {(position.takeProfitHit || []).length > 0 && (
                    <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-3">
                      <div className="text-xs font-bold text-gray-300 mb-2 flex items-center gap-1.5">
                        <SafeIcon icon={FiTarget} className="text-green-400" /> Auto Take-Profit Executed
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[...(position.takeProfitHit || [])].sort((a, b) => a - b).map(tier => (
                          <span key={tier} className="text-[10px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded border border-green-500/30 font-mono">
                            +{tier}% ✓
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Position Strategy Radar */}
                  <div className="space-y-4 relative">
                    {hasCustomSettings && (
                      <div className="absolute -top-3 -right-2 bg-blue-500 text-white text-[9px] font-bold px-2 py-0.5 rounded border border-blue-400 transform rotate-3 shadow-lg z-10">
                        CUSTOM STRATEGY
                      </div>
                    )}
                    <h4 className="text-white font-bold flex items-center gap-2 text-sm">
                      <SafeIcon icon={FiTarget} className="text-purple-400" /> Position Strategy Map
                    </h4>
                    
                    {/* Profit Milestones */}
                    <div className="bg-[#1c1c1d] border border-green-500/20 rounded-xl p-3 shadow-[0_0_10px_rgba(74,222,128,0.05)]">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-300">
                          <SafeIcon icon={FiAward} className="text-green-400" />
                          {nextMilestone ? `Next Profit Target (+${nextMilestone}%)` : 'All Targets Hit! 🚀'}
                        </div>
                        {nextTargetPrice && <div className="text-green-400 font-mono text-xs">{formatPrice(nextTargetPrice)}</div>}
                      </div>
                      <div className="h-2 w-full bg-[#0a0a0b] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-400 rounded-full transition-all duration-500"
                          style={{ width: `${milestoneProgress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-500 mt-1.5">
                        <span>+{prevMilestone}%</span>
                        <span>{nextMilestone ? `+${nextMilestone}%` : 'Moon 🌕'}</span>
                      </div>
                    </div>

                    {/* Trailing Stop Loss */}
                    <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-3">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-300">
                          <SafeIcon icon={FiTrendingUp} className={isTrailingActive ? "text-blue-400" : "text-gray-500"} />
                          {isTrailingActive ? `Trailing Stop (-${effTrailingSL}%)` : `Trailing Activation (${effTrailingAct}x)`}
                        </div>
                        <div className={`font-mono text-xs ${isTrailingActive ? 'text-blue-400' : 'text-gray-500'}`}>
                          {isTrailingActive ? formatPrice(trailingStopPrice) : formatPrice(activationPrice)}
                        </div>
                      </div>
                      <div className="h-2 w-full bg-[#0a0a0b] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${isTrailingActive ? 'bg-blue-400' : 'bg-gray-600'}`}
                          style={{ width: `${trailingProgress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-500 mt-1.5">
                        <span>{isTrailingActive ? 'Stop Price' : 'Entry'}</span>
                        <span>{isTrailingActive ? 'Peak Price' : 'Activation Target'}</span>
                      </div>
                    </div>

                    {/* Hard Stop Loss */}
                    <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-3">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-300">
                          <SafeIcon icon={FiShield} className="text-orange-400" />
                          Hard Stop (-{effHardSL}%)
                        </div>
                        <div className="text-orange-400 font-mono text-xs">{formatPrice(hardStopPrice)}</div>
                      </div>
                      <div className="h-2 w-full bg-[#0a0a0b] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${hardSlProgress < 20 ? 'bg-red-500' : 'bg-orange-400'}`}
                          style={{ width: `${hardSlProgress}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-[10px] text-gray-500 mt-1.5">
                        <span>Danger</span>
                        <span>{hardSlProgress >= 100 ? 'Safe (Above Entry)' : `${(100 - hardSlProgress).toFixed(0)}% to Hit`}</span>
                      </div>
                      {(position.liquidityQuoteSol || 0) > 0 && (() => {
                        const slippagePct = Math.min((position.amountSol / position.liquidityQuoteSol) * 100 * 2, 50);
                        if (slippagePct < 0.5) return null;
                        const realExit = hardStopPrice * (1 - slippagePct / 100);
                        return (
                          <div className="mt-1.5 text-[10px] font-mono flex justify-between text-red-400/70">
                            <span>Est. exit after {slippagePct.toFixed(1)}% slippage:</span>
                            <span className="font-bold text-red-400">{formatPrice(realExit)}</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div>
                    <h4 className="text-white font-bold flex items-center gap-2 text-sm mb-3">
                      <SafeIcon icon={FiClock} className="text-yellow-400" /> Instant Execution
                      {position.isReal && (
                        <span className="flex items-center gap-1 bg-green-500/20 border border-green-500/30 text-green-400 text-[8px] font-bold px-1.5 py-0.5 rounded uppercase">
                          <SafeIcon icon={FiZap} className="text-[8px]" /> Live
                        </span>
                      )}
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {[25, 50, 75, 100].map(percent => (
                        <button
                          key={percent}
                          onClick={() => handleSellClick(percent)}
                          disabled={position.isReal && realSellLoading}
                          className={`py-3 rounded-xl font-bold text-sm transition-colors border flex flex-col items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${percent === 100 ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20' : 'bg-[#2c2c2e] text-white border-[#3c3c3e] hover:bg-[#3c3c3e]'}`}
                        >
                          {position.isReal && realSellLoading ? '…' : `${percent}%`}
                        </button>
                      ))}
                    </div>
                    {position.isReal && (
                      <button
                        onClick={() => setForceCloseConfirm(true)}
                        className="w-full mt-2 py-2.5 rounded-xl font-bold text-xs transition-colors border border-orange-500/30 text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 flex items-center justify-center gap-2"
                      >
                        <SafeIcon icon={FiAlertTriangle} /> Force Close (clear stuck position)
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {activeTab === 'strategy' && (
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
                  
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-blue-400 text-xs leading-relaxed">
                    Override the global stop-loss settings for this specific position. Use the sliders to visually adjust your risk.
                  </div>

                  {/* Visual Hard SL Manager */}
                  <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-white font-bold text-sm flex items-center gap-2">
                        <SafeIcon icon={FiShield} className="text-orange-400" /> Hard Stop-Loss
                      </h4>
                      <div className="bg-[#0a0a0b] border border-[#3c3c3e] px-2 py-1 rounded text-orange-400 font-mono text-sm font-bold">
                        -{localHardSL}%
                      </div>
                    </div>
                    
                    <input 
                      type="range" 
                      min="1" max="99" 
                      value={localHardSL} 
                      onChange={(e) => setLocalHardSL(Number(e.target.value))}
                      className="w-full h-2 bg-[#0a0a0b] rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-orange-400 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(249,115,22,0.5)]"
                      style={{ background: `linear-gradient(to right, #f97316 ${localHardSL}%, #0a0a0b ${localHardSL}%)` }}
                    />
                    
                    <div className="flex justify-between mt-3 text-xs">
                      <div className="flex flex-col">
                        <span className="text-gray-500 uppercase tracking-wider text-[9px]">Entry Price</span>
                        <span className="text-white font-mono">{formatPrice(position.buyPrice)}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-orange-500 uppercase tracking-wider text-[9px]">Auto-Sell Price</span>
                        <span className="text-orange-400 font-mono font-bold">{formatPrice(position.buyPrice * (1 - localHardSL/100))}</span>
                      </div>
                    </div>
                  </div>

                  {/* Visual Trailing SL Manager */}
                  <div className="bg-[#1c1c1d] border border-[#2c2c2e] rounded-xl p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-white font-bold text-sm flex items-center gap-2">
                        <SafeIcon icon={FiTrendingUp} className="text-blue-400" /> Trailing Stop-Loss
                      </h4>
                      <div className="bg-[#0a0a0b] border border-[#3c3c3e] px-2 py-1 rounded text-blue-400 font-mono text-sm font-bold">
                        -{localTrailingSL}%
                      </div>
                    </div>
                    
                    <input 
                      type="range" 
                      min="1" max="99" 
                      value={localTrailingSL} 
                      onChange={(e) => setLocalTrailingSL(Number(e.target.value))}
                      className="w-full h-2 bg-[#0a0a0b] rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-blue-400 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                      style={{ background: `linear-gradient(to right, #3b82f6 ${localTrailingSL}%, #0a0a0b ${localTrailingSL}%)` }}
                    />
                    
                    <div className="flex justify-between mt-3 text-xs">
                      <div className="flex flex-col">
                        <span className="text-gray-500 uppercase tracking-wider text-[9px]">Peak Price</span>
                        <span className="text-white font-mono">{formatPrice(position.peakPrice)}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-blue-500 uppercase tracking-wider text-[9px]">Calculated Trail Stop</span>
                        <span className="text-blue-400 font-mono font-bold">{formatPrice(position.peakPrice * (1 - localTrailingSL/100))}</span>
                      </div>
                    </div>

                    <div className="mt-5 pt-4 border-t border-[#2c2c2e]">
                       <div className="flex justify-between items-center mb-3">
                        <h5 className="text-gray-400 font-semibold text-xs">Activation Multiplier</h5>
                        <div className="bg-[#0a0a0b] border border-[#3c3c3e] px-2 py-1 rounded text-white font-mono text-xs font-bold">
                          {localTrailingAct}x
                        </div>
                      </div>
                      <input 
                        type="range" 
                        min="1.1" max="10" step="0.1"
                        value={localTrailingAct} 
                        onChange={(e) => setLocalTrailingAct(Number(e.target.value))}
                        className="w-full h-2 bg-[#0a0a0b] rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
                        style={{ background: `linear-gradient(to right, #3c3c3e ${((localTrailingAct-1.1)/8.9)*100}%, #0a0a0b ${((localTrailingAct-1.1)/8.9)*100}%)` }}
                      />
                      <div className="flex justify-between mt-2 text-[10px] text-gray-500 font-mono">
                        <span>Activates at: {formatPrice(position.buyPrice * localTrailingAct)} SOL</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    {hasCustomSettings && (
                      <button 
                        onClick={handleResetStrategy}
                        className="flex-1 py-3 rounded-xl font-bold text-sm transition-colors border border-red-500/30 text-red-400 bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center gap-2"
                      >
                        <SafeIcon icon={FiRotateCcw} /> Reset to Global
                      </button>
                    )}
                    <button 
                      onClick={handleSaveStrategy}
                      className="flex-[2] py-3 rounded-xl font-bold text-sm transition-colors border border-blue-500 text-white bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
                    >
                      <SafeIcon icon={FiSave} /> Save Custom Strategy
                    </button>
                  </div>

                </motion.div>
              )}

            </div>
          </motion.div>
        </React.Fragment>
        )}
      </AnimatePresence>
      {/* Force Close Confirmation */}
      <AnimatePresence>
        {forceCloseConfirm && (
          <React.Fragment key="force-close-confirm">
            <motion.div key="fc-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[70] backdrop-blur-sm"
              onClick={() => setForceCloseConfirm(false)}
            />
            <div key="fc-modal" className="fixed inset-0 z-[80] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="bg-[#1c1c1d] border border-orange-500/30 rounded-2xl p-6 max-w-sm w-full pointer-events-auto shadow-xl"
              >
                <div className="flex flex-col items-center text-center mb-5">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-full flex items-center justify-center mb-3 border border-orange-500/20">
                    <SafeIcon icon={FiAlertTriangle} className="text-2xl text-orange-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">Force Close Position</h3>
                  <p className="text-gray-400 text-sm">
                    Removes <strong className="text-white">${position.symbol}</strong> from active positions.
                    The bot first tries to sell any remaining tokens; if there's nothing to sell
                    (already exited / dead token), it just clears the stuck position.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setForceCloseConfirm(false)}
                    className="flex-1 py-3 rounded-xl font-medium text-gray-400 bg-[#2c2c2e] hover:bg-[#3c3c3e] transition-colors border border-[#3c3c3e]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeForceClose}
                    disabled={forceCloseLoading}
                    className="flex-[2] py-3 rounded-xl font-bold text-white bg-orange-600 hover:bg-orange-500 disabled:opacity-50 transition-colors border border-orange-500"
                  >
                    {forceCloseLoading ? <span className="animate-pulse">Closing...</span> : 'Force Close'}
                  </button>
                </div>
              </motion.div>
            </div>
          </React.Fragment>
        )}
      </AnimatePresence>
    </>
  );
}